// Master Apps · custo estimado dos projetos no Railway.
//
// Contrato da API (admin only):
//   GET /api/railway/costs → 200 {
//     data: {
//       atualizadoEm: string | null,   // null só com semToken e sem snapshot
//       moeda: 'USD',
//       semToken: boolean,             // falta RAILWAY_API_TOKEN no ambiente
//       precoDiscoConfirmado: boolean, // false = disco/backup FORA do total
//       custos: [{ repo, projetoRailway, estimadoMesUsd, detalhe, quantidades }]
//     }
//   }
//   502 RAILWAY_ERROR quando a API do Railway falha e não há snapshot em cache.
//
// O número é ESTIMATIVA (uso do mês corrente × tabela de preços), não fatura.
// `detalhe` traz os CINCO componentes cobrados (memória, CPU, egress, disco e
// backup) e os cinco somam `estimadoMesUsd`.
//
// TRANSPORTE: `fetch` relativo em vez do client RPC, pelo mesmo motivo do
// `use-favorites.ts` — o `apiClient` (hono/client) só monta caminhos presentes
// no tipo `AppType` da API, e `api.railway.costs` ainda não está lá (a rota está
// sendo escrita em paralelo). O fetch repete exatamente o que o `apiClient` faz:
// Bearer da sessão do Clerk (ausente em dev, e aí a API trata como dev-user),
// `credentials: 'include'` e `cache: 'no-store'`. Quando o tipo expuser o
// caminho, trocar por `apiClient.api.railway.costs.$get()` — nada mais muda.
//
// CACHE: o backend guarda a resposta por 1 hora, então o front usa o MESMO
// `staleTime`. Nada de polling: custo não muda de minuto a minuto e consultar a
// API do Railway com mais frequência aumentaria consumo sem informar nada novo.
import { useQuery } from '@tanstack/react-query';
import { readApiError } from '@/hooks/use-applications';
import { getClerk } from '@/store/session';

// Mesma origem do `apiClient` (vazio = same-origin).
const baseUrl = import.meta.env.VITE_API_URL || '';

/** Cache de 1 hora — o mesmo do backend. */
export const RAILWAY_COSTS_STALE_MS = 1000 * 60 * 60;

/**
 * Quebra do custo por recurso cobrado — CINCO componentes, e os cinco somam
 * `estimadoMesUsd`. Disco e backup entram no total desde que o preço do volume
 * foi confirmado na tabela oficial do Railway; a tela mostra os cinco justamente
 * para as partes fecharem com o total (parte faltando parece erro de conta).
 */
export interface RailwayCostDetail {
  memoriaUsd: number;
  cpuUsd: number;
  egressUsd: number;
  discoUsd: number;
  backupUsd: number;
}

/** Quantidades consumidas no mês (o que multiplica a tabela de preços). */
export interface RailwayCostQuantities {
  memoriaGbMes: number;
  cpuVcpuMes: number;
  egressGb: number;
  discoGbMes: number;
  backupGbMes: number;
}

export interface RailwayProjectCost {
  /**
   * `owner/repo` do deploy — a CHAVE de amarração com a aplicação do inventário.
   * Projeto sem repositório configurado no Railway vem `null`/'' e cai na seção
   * de "não mapeados" da tela de gastos (ele existe e consome; esconder daria um
   * total falso).
   */
  repo: string | null;
  projetoRailway: string;
  estimadoMesUsd: number;
  detalhe: RailwayCostDetail;
  quantidades: RailwayCostQuantities;
}

/**
 * Cotação do dólar que acompanha o custo — vem do PTAX do Banco Central, já
 * cacheada no servidor (`apps/api/src/services/cotacao-dolar.ts`). O navegador
 * NUNCA fala com o Banco Central: são 46 slides e a apresentação roda em sala,
 * às vezes sem rede boa; uma chamada externa por slide seria 46 oportunidades de
 * o número piscar na parede.
 */
export interface CotacaoDolar {
  /** Quantos reais vale 1 dólar. */
  valor: number;
  /** Data da publicação PTAX, em 'YYYY-MM-DD'. */
  data: string;
  /** Ponta do PTAX ('venda'). */
  tipo: string;
  /** Origem do número, para a tela poder creditar a fonte. */
  fonte: string;
}

export interface RailwayCosts {
  /**
   * Quando o snapshot foi lido da API do Railway. `null` quando `semToken` é
   * true e nunca houve snapshot — nada de formatar data em cima de null.
   *
   * Quando a API do Railway falha MAS existe snapshot antigo, a rota devolve o
   * snapshot velho com o carimbo antigo (200, sem erro visível): é por isso que
   * a tela mostra a IDADE do dado, e não só a data.
   */
  atualizadoEm: string | null;
  moeda: string;
  /** true = `RAILWAY_API_TOKEN` não configurado: a lista vem vazia. */
  semToken: boolean;
  /** false = preço de disco/backup não confirmado, logo FORA do total. */
  precoDiscoConfirmado: boolean;
  /**
   * Cotação para exibir o custo em reais. `null` = não há cotação confiável
   * agora — e aí a regra é NÃO converter e NÃO cair para dólar (ver
   * `converterParaBrl`).
   */
  cotacao: CotacaoDolar | null;
  custos: RailwayProjectCost[];
}

/**
 * Converte um custo em dólar para reais pela cotação do envelope.
 *
 * As DUAS formas de devolver `null` são deliberadas e significam a mesma coisa
 * para a tela ("não sei, mostre —"):
 *  - `usd === null`: o custo ainda não chegou (ou a consulta falhou);
 *  - `cotacao === null`: o custo chegou, mas não há taxa confiável para
 *    converter. NÃO existe plano B de exibir o valor em dólar: a apresentação
 *    proíbe dólar, então o silêncio é a única saída honesta.
 *
 * `usd === 0` continua sendo 0 — zero é resposta legítima (aplicação fora da
 * nuvem não consome Railway) e não pode virar "—".
 */
export function converterParaBrl(
  usd: number | null | undefined,
  cotacao: CotacaoDolar | null | undefined,
): number | null {
  if (typeof usd !== 'number' || !Number.isFinite(usd)) return null;
  if (!cotacao) return null;
  return usd * cotacao.valor;
}

/**
 * Normaliza a cotação vinda da API. Valor não numérico, não finito ou ≤ 0 vira
 * `null`: preferimos a tela sem número a uma multiplicação por lixo — um
 * `NaN`/`0` escaparia daqui e apareceria como "R$ 0,00" em 44 slides, que é
 * exatamente o "número errado" que não pode acontecer.
 */
function normalizarCotacao(raw: unknown): CotacaoDolar | null {
  const c = raw as Partial<CotacaoDolar> | null | undefined;
  if (!c || typeof c.valor !== 'number' || !Number.isFinite(c.valor) || c.valor <= 0) return null;
  return {
    valor: c.valor,
    data: typeof c.data === 'string' ? c.data : '',
    tipo: typeof c.tipo === 'string' ? c.tipo : '',
    fonte: typeof c.fonte === 'string' ? c.fonte : '',
  };
}

/**
 * Slug comparável de um repositório, seja ele URL completa
 * (`https://github.com/masterboiteam/app-master-apps`, com ou sem `.git`, com ou
 * sem barra final) ou `owner/repo` (`masterboiteam/app-master-apps`): o ÚLTIMO
 * segmento do caminho, em minúsculas.
 *
 * É o último segmento, e não o par `owner/repo`, porque os dois lados escrevem o
 * dono de formas diferentes (a URL cadastrada pode ter o host, o Railway devolve
 * `owner/repo`) — o nome do repositório é o que os dois têm em comum. Caixa
 * ignorada de propósito: o GitHub não diferencia.
 */
export function repoSlug(repo: string | null | undefined): string | null {
  if (!repo) return null;
  const clean = repo
    .trim()
    .replace(/[?#].*$/, '')
    .replace(/\.git$/i, '')
    .replace(/\/+$/, '');
  if (clean === '') return null;
  const last = clean.split('/').pop();
  if (!last) return null;
  const slug = last.trim().toLowerCase();
  return slug === '' ? null : slug;
}

/**
 * Índice slug do repositório → custo do projeto. Projeto sem repositório fica
 * FORA do índice (é justamente o "não mapeado"); repositório repetido mantém a
 * primeira ocorrência, que é a ordem que o backend devolve.
 */
export function costsByRepoSlug(
  custos: readonly RailwayProjectCost[] | undefined,
): Map<string, RailwayProjectCost> {
  const map = new Map<string, RailwayProjectCost>();
  for (const c of custos ?? []) {
    const slug = repoSlug(c.repo);
    if (slug && !map.has(slug)) map.set(slug, c);
  }
  return map;
}

/** Projetos que o Railway roda SEM repositório associado (consomem, mas não casam). */
export function unmappedCosts(costs: RailwayCosts | undefined): RailwayProjectCost[] {
  return (costs?.custos ?? []).filter((c) => repoSlug(c.repo) === null);
}

/** Total do workspace — soma de TODOS os projetos, mapeados ou não. */
export function totalUsd(costs: RailwayCosts | undefined): number {
  return (costs?.custos ?? []).reduce((acc, c) => acc + (c.estimadoMesUsd || 0), 0);
}

async function fetchRailwayCosts(): Promise<RailwayCosts> {
  const headers = new Headers();
  try {
    const token = await getClerk()?.session?.getToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  } catch {
    // sem token (não logado ou Clerk indisponível) — segue sem header
  }

  const res = await fetch(`${baseUrl}/api/railway/costs`, {
    headers,
    credentials: 'include',
    cache: 'no-store',
  });
  if (!res.ok) throw await readApiError(res, 'Falha ao carregar os custos do Railway');

  const json = (await res.json().catch(() => null)) as { data?: Partial<RailwayCosts> } | null;
  const data = json?.data;
  // Normaliza a forma antes de entregar à UI: a rota é nova e um campo ausente
  // não pode virar `undefined.map` no meio da tabela.
  return {
    atualizadoEm: typeof data?.atualizadoEm === 'string' ? data.atualizadoEm : null,
    moeda: typeof data?.moeda === 'string' ? data.moeda : 'USD',
    semToken: data?.semToken === true,
    precoDiscoConfirmado: data?.precoDiscoConfirmado === true,
    cotacao: normalizarCotacao(data?.cotacao),
    custos: Array.isArray(data?.custos) ? data.custos : [],
  };
}

/**
 * `GET /api/railway/costs`.
 *
 * `enabled` desliga a consulta para quem não é admin: custo é dado financeiro e
 * o catálogo é visto por todos os colaboradores — sem isso, um colaborador
 * levaria 403 em cada abertura do catálogo (barulho no log e no rate limit).
 *
 * DEGRADAÇÃO GRACIOSA é requisito, não detalhe: enquanto a rota não existir, a
 * query falha com 404 e as telas mostram "—". Nenhum chamador deve tratar erro
 * de custo como erro de tela — a exceção é a tela de gastos, cujo assunto É o
 * custo.
 */
export function useRailwayCosts(enabled = true) {
  return useQuery<RailwayCosts>({
    queryKey: ['railway-costs'],
    queryFn: fetchRailwayCosts,
    enabled,
    staleTime: RAILWAY_COSTS_STALE_MS,
    // Mantém o dado em memória além do stale para navegar entre catálogo,
    // inventário e gastos sem uma ida nova à API a cada troca de tela.
    gcTime: RAILWAY_COSTS_STALE_MS * 2,
  });
}
